import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Eye, ClipboardText, Users, Warning, Signature, FilePdf } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Bitácora de turno de taller — jefe de taller ve la foto operativa
 * llenada por el supervisor al cerrar turno.
 */
const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  project_id: defaultProject(),
  shift: "Día",
  supervisor: "",
  headcount_present: 0,
  headcount_expected: 0,
  absentees: "",
  equipment_in: "",
  equipment_out: "",
  equipment_status: "",
  ots_closed: 0,
  ots_opened: 0,
  new_failures: "",
  oil_consumed_l: 0,
  coolant_consumed_l: 0,
  filters_used: 0,
  other_consumables: "",
  incidents: "",
  risks_observed: "",
  pending_next_shift: "",
  notes: "",
  signature: "",
});

export default function ShiftLogbookPanel({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("closed"); // "closed" | "create" | "edit" | "view"
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [weekStart, setWeekStart] = useState(() => {
    // Lunes de la semana actual
    const d = new Date();
    const day = d.getDay(); // 0=Dom, 1=Lun…
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
  });

  const load = () =>
    api.get("/maintenance/shift-logbook")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.toLowerCase();
    return rows.filter((r) =>
      `${r.date} ${r.shift} ${r.supervisor}`.toLowerCase().includes(q));
  }, [rows, filter]);

  const openNew = () => { setForm(emptyForm()); setMode("create"); };
  const openView = (r) => { setForm({ ...r }); setMode("view"); };
  const openEdit = (r) => { setForm({ ...r }); setMode("edit"); };
  const close = () => { setMode("closed"); setForm(emptyForm()); };

  const save = async () => {
    if (!form.date || !form.supervisor.trim()) {
      toast.error("Fecha y supervisor son obligatorios");
      return;
    }
    setBusy(true);
    try {
      if (!assertProject(form.project_id)) return;
      if (mode === "edit" && form.id) {
        await api.put(`/maintenance/shift-logbook/${form.id}`, form);
        toast.success("Bitácora actualizada");
      } else {
        await api.post("/maintenance/shift-logbook", form);
        toast.success("Bitácora registrada");
      }
      close();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta bitácora de turno? Queda registrada en el historial.")) return;
    try {
      await api.delete(`/maintenance/shift-logbook/${id}`, {
        headers: { "X-Delete-Reason": "Eliminada desde Bitácora de turno" },
      });
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const totals = useMemo(() => {
    const n = rows.length;
    if (!n) return null;
    const acc = { ots_closed: 0, ots_opened: 0, oil_l: 0, filters: 0, headcount_avg: 0 };
    let counted = 0;
    for (const r of rows.slice(0, 30)) {
      acc.ots_closed += Number(r.ots_closed || 0);
      acc.ots_opened += Number(r.ots_opened || 0);
      acc.oil_l += Number(r.oil_consumed_l || 0);
      acc.filters += Number(r.filters_used || 0);
      if (r.headcount_present) { acc.headcount_avg += Number(r.headcount_present); counted++; }
    }
    acc.headcount_avg = counted ? (acc.headcount_avg / counted).toFixed(1) : "0";
    return acc;
  }, [rows]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  return (
    <div className="space-y-4" data-testid="mnt-shift-logbook-panel">
      {/* Header + KPIs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-black text-xl flex items-center gap-2">
            <ClipboardText size={22} weight="fill" className="text-blue-900" />
            Bitácora de turno · Taller
          </h3>
          <p className="text-xs text-slate-500">Cierre operativo del turno para el jefe de taller.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Filtrar por fecha/supervisor…" value={filter}
                  onChange={(e) => setFilter(e.target.value)} className="max-w-xs"
                  data-testid="mnt-shift-filter" />
          <div className="hidden sm:flex items-center gap-1 border-l border-slate-200 pl-2">
            <Label className="text-[10px] uppercase text-slate-500 tracking-widest whitespace-nowrap">Semana</Label>
            <Input type="date" value={weekStart}
                    onChange={(e) => setWeekStart(e.target.value)}
                    className="w-36 h-9 text-xs" data-testid="mnt-shift-week-start" />
            <Button variant="outline" size="sm"
                     onClick={async () => {
                       try {
                         const r = await api.get("/maintenance/shift-logbook/weekly-pdf", {
                           params: { week_start: weekStart }, responseType: "blob",
                         });
                         downloadFile(r.data, `bitacora-taller-semana-${weekStart}.pdf`);
                       } catch (e) { toast.error(apiError(e)); }
                     }}
                     className="gap-1.5 border-orange-300 text-orange-800 hover:bg-orange-50"
                     data-testid="mnt-shift-weekly-pdf">
              <FilePdf size={14} weight="bold" /> PDF semanal
            </Button>
          </div>
          {canWrite && (
            <Button onClick={openNew} data-testid="mnt-shift-new" className="bg-blue-900 hover:bg-blue-800 gap-1.5">
              <Plus size={14} weight="bold" /> Nueva bitácora
            </Button>
          )}
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="mnt-shift-kpis">
          <Kpi label="Turnos (30d)" value={rows.length} />
          <Kpi label="OTs cerradas" value={totals.ots_closed} />
          <Kpi label="OTs abiertas" value={totals.ots_opened} tone="amber" />
          <Kpi label="Aceite (L)" value={totals.oil_l.toFixed(1)} />
          <Kpi label="Headcount prom." value={totals.headcount_avg} />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-[11px] uppercase font-bold text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Turno</th>
              <th className="text-left px-3 py-2">Supervisor</th>
              <th className="text-center px-3 py-2">Personal (P/E)</th>
              <th className="text-center px-3 py-2">OTs C/A</th>
              <th className="text-left px-3 py-2">Pendiente próximo turno</th>
              <th className="text-right px-3 py-2 w-24">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Sin bitácoras — cierra un turno para empezar</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} onClick={() => openView(r)}
                   className="hover:bg-blue-50/40 cursor-pointer"
                   data-testid={`mnt-shift-row-${r.id}`}
                   title="Click para ver detalle">
                <td className="px-3 py-2 font-mono">{r.date} <ProjectBadge id={r.project_id} className="ml-1 align-middle" /></td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{r.shift}</span>
                </td>
                <td className="px-3 py-2 text-slate-800">{r.supervisor}</td>
                <td className="text-center px-3 py-2 font-mono">
                  <span className={r.headcount_present < r.headcount_expected ? "text-amber-700 font-bold" : "text-slate-700"}>
                    {r.headcount_present}
                  </span>
                  <span className="text-slate-400">/{r.headcount_expected}</span>
                </td>
                <td className="text-center px-3 py-2 font-mono">
                  <span className="text-emerald-700 font-bold">{r.ots_closed}</span>
                  /<span className="text-amber-700 font-bold">{r.ots_opened}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate">
                  {r.pending_next_shift || <span className="text-slate-400 italic">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openView(r)} className="text-slate-500 hover:text-blue-700 p-1"
                             data-testid={`mnt-shift-view-${r.id}`} title="Ver detalle">
                      <Eye size={14} />
                    </button>
                    {canWrite && (
                      <>
                        <button onClick={() => openEdit(r)} className="text-slate-500 hover:text-blue-700 p-1"
                                 data-testid={`mnt-shift-edit-${r.id}`} title="Editar">
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"
                                 data-testid={`mnt-shift-del-${r.id}`} title="Eliminar">
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={mode !== "closed"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto"
                        aria-describedby="mnt-shift-dlg-desc"
                        data-testid="mnt-shift-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {mode === "view" && `Turno ${form.date} · ${form.shift}`}
              {mode === "create" && "Nueva bitácora de turno"}
              {mode === "edit" && `Editar turno ${form.date}`}
            </DialogTitle>
            <p id="mnt-shift-dlg-desc" className="text-xs text-slate-500">
              Foto operativa del turno para el jefe de taller — personal, equipos, consumibles, pendientes.
            </p>
          </DialogHeader>
          {mode !== "view" && <ProjectField value={form.project_id} onChange={(v) => patch({ project_id: v })} testid="mnt-shift-project" />}
          <ShiftForm form={form} patch={patch} readOnly={mode === "view"} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Cerrar</Button>
            {mode === "view" && canWrite && (
              <Button onClick={() => setMode("edit")} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                       data-testid="mnt-shift-detail-edit">
                <PencilSimple size={14} /> Editar
              </Button>
            )}
            {(mode === "create" || mode === "edit") && (
              <Button onClick={save} disabled={busy || !form.date || !form.supervisor.trim()}
                       className="bg-blue-900 hover:bg-blue-800"
                       data-testid="mnt-shift-save">
                {busy ? "…" : (mode === "edit" ? "Guardar cambios" : "Registrar")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900",
    amber: "text-amber-700",
    red: "text-red-700",
    green: "text-emerald-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`font-heading font-black text-2xl tabular-nums mt-1 ${tones[tone] || tones.slate}`}>{value}</p>
    </div>
  );
}

function ShiftForm({ form, patch, readOnly }) {
  const cls = readOnly ? "bg-slate-50" : "";
  const setNum = (k) => (e) => patch({ [k]: Number(e.target.value) });
  const setStr = (k) => (e) => patch({ [k]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Field label="Fecha">
          <Input type="date" value={form.date} onChange={setStr("date")} readOnly={readOnly} className={cls} data-testid="mnt-shift-date" />
        </Field>
        <Field label="Turno">
          {readOnly ? (
            <Input value={form.shift} readOnly className={cls} />
          ) : (
            <Select value={form.shift} onValueChange={(v) => patch({ shift: v })}>
              <SelectTrigger data-testid="mnt-shift-shift"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Día">Día</SelectItem>
                <SelectItem value="Noche">Noche</SelectItem>
                <SelectItem value="A">Cuadrilla A</SelectItem>
                <SelectItem value="B">Cuadrilla B</SelectItem>
                <SelectItem value="C">Cuadrilla C</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Supervisor a cargo">
          <Input value={form.supervisor} onChange={setStr("supervisor")} readOnly={readOnly} className={cls} data-testid="mnt-shift-supervisor" />
        </Field>
      </div>

      {/* Personal */}
      <Section title="Personal" icon={Users}>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Presentes"><Input type="number" min={0} value={form.headcount_present ?? 0} onChange={setNum("headcount_present")} readOnly={readOnly} className={cls} /></Field>
          <Field label="Esperados"><Input type="number" min={0} value={form.headcount_expected ?? 0} onChange={setNum("headcount_expected")} readOnly={readOnly} className={cls} /></Field>
          <div />
          <div className="col-span-3">
            <Field label="Ausencias (nombre + motivo)"><Textarea rows={2} value={form.absentees || ""} onChange={setStr("absentees")} readOnly={readOnly} className={cls} placeholder="Juan Pérez — enfermedad; Carlos Ruiz — permiso" /></Field>
          </div>
        </div>
      </Section>

      {/* Equipos */}
      <Section title="Equipos" icon={ClipboardText}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Entraron a taller"><Textarea rows={2} value={form.equipment_in || ""} onChange={setStr("equipment_in")} readOnly={readOnly} className={cls} placeholder="SC-01, JU-03…" /></Field>
          <Field label="Salieron operativos"><Textarea rows={2} value={form.equipment_out || ""} onChange={setStr("equipment_out")} readOnly={readOnly} className={cls} placeholder="CB-02, JA-01…" /></Field>
          <div className="col-span-2">
            <Field label="Estado (inicio/fin)"><Textarea rows={3} value={form.equipment_status || ""} onChange={setStr("equipment_status")} readOnly={readOnly} className={cls} placeholder="SC-01 llegó con fuga; salió reparado…" /></Field>
          </div>
        </div>
      </Section>

      {/* OTs */}
      <Section title="OTs y fallas">
        <div className="grid grid-cols-2 gap-3">
          <Field label="OTs cerradas en el turno"><Input type="number" min={0} value={form.ots_closed ?? 0} onChange={setNum("ots_closed")} readOnly={readOnly} className={cls} /></Field>
          <Field label="OTs abiertas en el turno"><Input type="number" min={0} value={form.ots_opened ?? 0} onChange={setNum("ots_opened")} readOnly={readOnly} className={cls} /></Field>
          <div className="col-span-2">
            <Field label="Fallas nuevas detectadas"><Textarea rows={2} value={form.new_failures || ""} onChange={setStr("new_failures")} readOnly={readOnly} className={cls} placeholder="SC-02 fuga hidráulica, presión baja…" /></Field>
          </div>
        </div>
      </Section>

      {/* Consumibles */}
      <Section title="Consumo de insumos">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Aceite (L)"><Input type="number" step="0.1" min={0} value={form.oil_consumed_l ?? 0} onChange={setNum("oil_consumed_l")} readOnly={readOnly} className={cls} /></Field>
          <Field label="Refrigerante (L)"><Input type="number" step="0.1" min={0} value={form.coolant_consumed_l ?? 0} onChange={setNum("coolant_consumed_l")} readOnly={readOnly} className={cls} /></Field>
          <Field label="Filtros usados"><Input type="number" min={0} value={form.filters_used ?? 0} onChange={setNum("filters_used")} readOnly={readOnly} className={cls} /></Field>
          <div className="col-span-3">
            <Field label="Otros insumos"><Textarea rows={2} value={form.other_consumables || ""} onChange={setStr("other_consumables")} readOnly={readOnly} className={cls} placeholder="Grasa 5kg, sellos hidráulicos x3" /></Field>
          </div>
        </div>
      </Section>

      {/* Seguridad */}
      <Section title="Seguridad" icon={Warning}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Incidentes ocurridos"><Textarea rows={2} value={form.incidents || ""} onChange={setStr("incidents")} readOnly={readOnly} className={cls} placeholder="Ninguno / describe" /></Field>
          <Field label="Riesgos observados"><Textarea rows={2} value={form.risks_observed || ""} onChange={setStr("risks_observed")} readOnly={readOnly} className={cls} placeholder="Piso mojado zona jumbo, andamio flojo…" /></Field>
        </div>
      </Section>

      {/* Continuidad */}
      <Section title="Continuidad">
        <div className="grid gap-3">
          <Field label="Pendiente para el siguiente turno *">
            <Textarea rows={3} value={form.pending_next_shift || ""} onChange={setStr("pending_next_shift")} readOnly={readOnly} className={cls} placeholder="Terminar cambio de filtro en SC-01…" />
          </Field>
          <Field label="Notas adicionales">
            <Textarea rows={2} value={form.notes || ""} onChange={setStr("notes")} readOnly={readOnly} className={cls} placeholder="Cualquier detalle relevante" />
          </Field>
        </div>
      </Section>

      {/* Firma del supervisor */}
      <Section title="Firma del supervisor" icon={Signature}>
        {readOnly ? (
          form.signature ? (
            <div className="border border-slate-200 bg-white rounded p-2 inline-block"
                  data-testid="mnt-shift-signature-preview">
              <img src={form.signature} alt="Firma del supervisor"
                    className="max-h-24" />
              <p className="text-[10px] text-slate-500 mt-1">
                Firmado por <b>{form.supervisor || "supervisor"}</b>
              </p>
            </div>
          ) : (
            <p className="text-xs text-amber-600 italic">Sin firma registrada</p>
          )
        ) : (
          <div>
            <SignaturePad
              value={form.signature || ""}
              onChange={(v) => patch({ signature: v })}
              width={360} height={110}
              testid="mnt-shift-signature-pad"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              La firma valida el cierre de turno y queda anexada al PDF semanal.
            </p>
          </div>
        )}
      </Section>

      {form.created_by && (
        <p className="text-[10px] text-slate-400">
          Registrado {(form.created_at || "").slice(0, 16).replace("T", " ")} por {form.created_by}
          {form.updated_by && ` · Editado por ${form.updated_by} el ${(form.updated_at || "").slice(0, 16).replace("T", " ")}`}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="overline-label">{label}</Label>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon size={14} weight="fill" className="text-slate-500" />}
        <h4 className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{title}</h4>
      </div>
      {children}
    </div>
  );
}
